export default function WestPalmDestinationsSection() {
  const stats = [
    {
      value: "52%",
      label: "of West Palm Beach requests are Caribbean cruise or resort departures with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for business trips to Latin America from West Palm Beach professionals",
    },
    {
      value: "Nov–Apr",
      label: "peak cruise season — passport renewals spike just before holiday and spring break sailings",
    },
    {
      value: "48-hour",
      label: "shortest cruise departure window we've accommodated for a West Palm Beach family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for West Palm Beach travelers — whether by cruise from Port Everglades or Fort Lauderdale, or by direct flight from PBI to Nassau. Families, couples, and groups of friends from West Palm Beach regularly sail to the Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for West Palm Beach families seeking beach vacations within a short flight. PBI offers connecting service through Atlanta, Charlotte, and Houston. Business travelers also fly to Mexico City and Guadalajara for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw West Palm Beach business travelers for finance, healthcare, and tech conferences. Canadian ski destinations like Whistler and Banff attract winter sports enthusiasts. Canada requires a valid passport for all air arrivals, and many West Palm Beach families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom",
      context: "London is a frequent destination for West Palm Beach arts patrons attending gallery openings, theater premieres, and museum exhibitions. The Kravis Center and Norton Museum both maintain relationships with UK cultural institutions that facilitate exchanges and group travel. The UK requires passports valid for the duration of your stay.",
      urgency: "Arts event tickets are often non-refundable — passport delays can cost hundreds in lost reservations",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "France & Italy",
      context: "Paris for fashion, art, and dining; Rome and Florence for cultural tours and culinary experiences. West Palm Beach arts patrons and affluent families regularly travel to France and Italy for extended vacations, villa rentals, and cultural immersion. The Schengen Area requires three months of validity beyond your planned departure.",
      urgency: "Villa rental deposits and tour bookings are typically non-refundable with short cancellation windows",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Latin America",
      context: "Sao Paulo, Rio de Janeiro, Bogota, and Panama City draw West Palm Beach business travelers in finance, healthcare, and real estate. Costa Rica and Belize attract eco-tourism and adventure travelers from the younger professional demographic. Each country has its own passport validity requirements, typically six months.",
      urgency: "Business trips to Latin America are often booked with 2–4 weeks of notice — too short for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where West Palm Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from West Palm Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            West Palm Beach travelers head to the Caribbean, Latin America, Europe, and Canada for a mix of business, leisure, cruises, and cultural experiences. These are the destinations that create the most passport urgency for our West Palm Beach clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Palm Beach", href: "/cities/palm-beach/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Delray Beach", href: "/cities/delray-beach/" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Miami", href: "/cities/miami/" },
              { name: "Naples", href: "/cities/naples/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}